import { useCallback } from "react";
import { useFlowState } from "@/hooks/useFlowState";
import { useSignInAndCreateWorkflow } from "@/hooks/useSignInAndCreateWorkflow";
import { usePredictionsForFileAndFrameSegment } from "@/store";
import { useCurrentWorkflow } from "@/hooks/useCurrentWorkflow";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { DeploySteps, Flows } from "@/types/flows";
import { setCachedInferenceResults } from "@/utils/api/datasets";

/**
 * Shared hook for navigating to the Improve step with cached predictions.
 * Used by both ImproveActionCard and IntegrateSideMenu.
 *
 * When no workflow exists, this will:
 * 1. Sign in the user (if needed)
 * 2. Create a new workflow with inferred frames uploaded to the "rapid-review" batch (hardcoded on backend)
 * 3. Navigate to the Improve step
 */
export function useImproveModel() {
    const { setStep } = useFlowState();
    const { currentWorkflow: workflow } = useCurrentWorkflow();
    const { currentWorkspace: workspace } = useCurrentWorkspace();
    const predictions = usePredictionsForFileAndFrameSegment();

    // Configure the hook to navigate to IMPROVE step
    // Note: The backend hardcodes the "rapid-review" batch name in firstModelSetupService
    const { signInAndCreateWorkflow, loading } = useSignInAndCreateWorkflow({
        targetStep: DeploySteps.IMPROVE,
        isSync: true // Wait for upload to complete before navigating
    });

    const handleImprove = useCallback(() => {
        if (workflow && workspace) {
            // Workflow already exists - cache predictions and navigate to Improve
            if (workflow.url && Object.keys(predictions).length > 0) {
                setCachedInferenceResults(workflow.url, { predictions, annotations: {} });
            }

            setStep({
                flow: Flows.DEPLOY,
                step: DeploySteps.IMPROVE,
                workspaceUrl: workspace.url,
                workflowUrl: workflow.url
            });
        } else {
            // No workflow - create one with frames uploaded to rapid-review batch
            signInAndCreateWorkflow();
        }
    }, [workflow, workspace, predictions, setStep, signInAndCreateWorkflow]);

    return { handleImprove, loading };
}
